import { useEffect, useState } from 'react'
import { useIsMutating, useQueryClient } from '@tanstack/react-query'
import { enrollmentKeys, enrollmentMutationKey, useEnrollment } from '../api/enrollment'
import { useKycVerification, useSyncKycVerification, verificationKeys } from '../api/verification'
import { onboardingStatus, reviewingDescription } from '../model/onboarding-status'
import { Banner, Button, StatusBadge } from '@shared/design-system/ui'
import { useFeatureFlag } from '@shared/feature-flags/useFeatureFlag'
import { getUserFacingError } from '@shared/lib/errors/user-facing-error'
import { toast } from '@shared/lib/toast/toast-store'
import { useSession } from '@shared/lib/auth/session-store'
import { userKeys } from '@entities/user'
import { header, heading, page, subtitle, title } from './onboarding.css'
import { ProfileSection } from './ProfileSection'
import { verificationFailureMessage } from './profile-form'
import { OperationalStatusSection } from './OperationalStatusSection'
import { VerificationPanel, IDENTITY_SECTION_ANCHOR } from './VerificationPanel'
import { BankAccountSection, BANK_SECTION_ANCHOR } from './BankAccountSection'
import { getRequirementSummaryLabel } from '../lib/requirement-label'
import { formatDate } from '../lib/verification-dates'
import type { KycOnboarding } from '../api/verification'

const PROFILE_SECTION_ANCHOR = 'dados-cadastrais'
const OPERATIONAL_SECTION_ANCHOR = 'situacao-operacional'

function sectionAnchorForNextStep(onboarding: KycOnboarding | undefined): string {
  switch (onboarding?.nextStep.code) {
    case 'save_profile':
    case 'fix_field':
      return PROFILE_SECTION_ANCHOR
    case 'wait_review':
      return onboarding.steps.profile === 'in_review'
        ? PROFILE_SECTION_ANCHOR
        : IDENTITY_SECTION_ANCHOR
    case 'identity_document':
    case 'rejected':
    case 'activation_failed':
    case 'unsupported_requirement':
    case 'account_disabled':
      return IDENTITY_SECTION_ANCHOR
    case 'add_bank_account':
    case 'fix_bank_account':
      return BANK_SECTION_ANCHOR
    default:
      return OPERATIONAL_SECTION_ANCHOR
  }
}

function firstFlaggedProfileField(section: HTMLElement): HTMLElement | null {
  for (const field of section.querySelectorAll<HTMLElement>(
    '[aria-invalid="true"], [data-profile-check-hint="true"]',
  )) {
    if (field.closest('[hidden]')) continue
    // SelectField puts custom attributes on its native select; scroll to its visible control.
    return field.getAttribute('aria-hidden') === 'true'
      ? (field.parentElement?.querySelector<HTMLElement>('[aria-haspopup="listbox"]') ?? field)
      : field
  }
  return null
}

export function OnboardingPage() {
  const registrationFeedback = useFeatureFlag('connectRegistrationFeedback')
  const queryClient = useQueryClient()
  const enrollment = useEnrollment()
  const user = useSession((state) => state.user)
  const activationInProgress = useIsMutating({ mutationKey: enrollmentMutationKey }) > 0
  const verificationEnabled = enrollment.data?.status === 'account_linked'
  const verification = useKycVerification(verificationEnabled)
  const details = verification.data?.verification
  const onboarding = verification.data?.onboarding
  const pendingVerification = onboarding?.steps.identity === 'in_review'
  // Stripe's review spans steps, so it shows once here. An approved account re-checking an edit
  // shows it next to the profile instead.
  const reviewing =
    onboarding?.badge === 'approved' ? null : reviewingDescription(onboarding?.reviewing ?? [])
  const sellerActionOpen = Object.values(onboarding?.steps ?? {}).some(
    (step) => step === 'pending' || step === 'fix',
  )
  const inReview = onboarding?.badge === 'in_review' || reviewing !== null
  const syncVerification = useSyncKycVerification()
  const refreshing = verification.isFetching || syncVerification.isPending
  const [profileExpanded, setProfileExpanded] = useState(false)
  const [profileEditRequest, setProfileEditRequest] = useState(0)
  const [now, setNow] = useState(() => Date.now())
  const nextStep = onboarding?.nextStep
  const configurationUnavailable =
    enrollment.data?.eligibility.allowed === false &&
    enrollment.data.eligibility.reason === 'configuration_unavailable'
  const activationFailed =
    enrollment.data?.status === 'creation_failed' && enrollment.data.retryAvailable === false
  const supportRequired =
    configurationUnavailable || activationFailed || nextStep?.owner === 'support'
  const correctionRequired =
    !supportRequired &&
    (onboarding?.steps.profile === 'fix' ||
      onboarding?.steps.identity === 'fix' ||
      onboarding?.steps.bank === 'fix')
  const correctionLabel =
    nextStep?.code === 'fix_field' && nextStep.requirementId
      ? getRequirementSummaryLabel(nextStep.requirementId, 'form')
      : undefined
  const correctionRequirement =
    nextStep?.code === 'fix_field'
      ? details?.requirements.form.find(({ id }) => id === nextStep.requirementId)
      : undefined
  const confirmationFailure =
    registrationFeedback && correctionRequirement
      ? verificationFailureMessage(correctionRequirement)
      : null
  const correctionMessage = correctionLabel
    ? `Confira ${correctionLabel} para continuar seu cadastro.`
    : details?.currentDeadline
      ? `Confira as pendências do cadastro até ${formatDate(details.currentDeadline)}`
      : 'Confira as pendências indicadas abaixo'
  const status = onboardingStatus(onboarding)
  const onboardingSignature = onboarding ? JSON.stringify(onboarding.operations) : null
  const verificationStale =
    registrationFeedback &&
    Boolean(
      verification.data &&
      (verification.isError || details?.syncFailure || verification.data.eligibility.syncFailure),
    )
  const supportFeedback = registrationFeedback
    ? registrationSupportFeedback(
        activationFailed ? 'activation_failed' : nextStep?.code,
        configurationUnavailable,
        verificationStale,
      )
    : null

  function refreshVerification() {
    syncVerification.mutate(
      undefined,
      registrationFeedback
        ? {
            onError: (error) =>
              toast.error(
                getUserFacingError(error, { title: 'Não foi possível atualizar o cadastro' }),
              ),
          }
        : undefined,
    )
  }

  function handleProfileSaveSuccess(onboardingAfterSave: KycOnboarding | undefined) {
    const nextStep = onboardingAfterSave?.nextStep
    const sectionId = sectionAnchorForNextStep(onboardingAfterSave)
    window.requestAnimationFrame(() => {
      const section = document.getElementById(sectionId)
      if (!section) return
      const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
      const scrollTarget =
        nextStep?.code === 'save_profile' || nextStep?.code === 'fix_field'
          ? (firstFlaggedProfileField(section) ?? section)
          : section
      scrollTarget.scrollIntoView?.({ block: 'start', behavior: reduceMotion ? 'auto' : 'smooth' })
      section.querySelector<HTMLElement>('h2')?.focus({ preventScroll: true })
    })
  }

  useEffect(() => {
    if (enrollment.data?.status !== 'account_linked') return
    void queryClient.invalidateQueries({ queryKey: enrollmentKeys.view })
    void queryClient.invalidateQueries({ queryKey: verificationKeys.view })
  }, [enrollment.data?.status, queryClient])

  useEffect(() => {
    if (!inReview) return
    const timer = window.setInterval(() => setNow(Date.now()), 60_000)
    return () => window.clearInterval(timer)
  }, [inReview])

  useEffect(() => {
    if (!onboardingSignature) return
    void queryClient.invalidateQueries({ queryKey: userKeys.me })
  }, [onboardingSignature, queryClient])

  return (
    <div className={page} data-testid="onboarding-page">
      <header className={header}>
        <div className={heading}>
          <h1 className={title}>Cadastro e documentação</h1>
          <p className={subtitle}>
            Informe os dados da pessoa ou empresa responsável pelo cadastro.
          </p>
        </div>
        <StatusBadge tone={status.tone} label={status.label} />
      </header>
      {enrollment.isError ? (
        <Banner tone="danger" title="Não foi possível consultar o cadastro">
          {
            getUserFacingError(
              enrollment.error,
              registrationFeedback ? {} : { fallback: 'Atualize a página e tente novamente.' },
            ).description
          }
        </Banner>
      ) : null}
      <Banner tone="info" title="Cadastro protegido pela VendePay">
        Seus dados são usados apenas para validar sua conta e habilitar suas vendas e recebimentos.
      </Banner>
      {verificationStale ? (
        <Banner
          tone="warning"
          title="Consulta do cadastro desatualizada"
          data-testid="onboarding-stale-feedback"
        >
          Não foi possível atualizar o cadastro. A última situação conhecida continua visível.
        </Banner>
      ) : null}
      {supportRequired ? (
        <Banner
          tone={nextStep?.code === 'rejected' ? 'danger' : 'warning'}
          title={supportFeedback?.title ?? 'Não conseguimos concluir seu cadastro automaticamente'}
          data-testid="onboarding-support-feedback"
        >
          {supportFeedback?.description ??
            'Fale com o suporte da VendePay para continuarmos a análise.'}
        </Banner>
      ) : correctionRequired ? (
        <Banner
          tone="warning"
          title={
            confirmationFailure
              ? 'Não foi possível confirmar os dados'
              : 'Precisamos de uma correção'
          }
          data-testid="onboarding-correction-feedback"
        >
          {confirmationFailure ?? correctionMessage}
        </Banner>
      ) : inReview && !sellerActionOpen ? (
        <Banner
          tone="info"
          title="Estamos verificando seu cadastro"
          action={
            <Button
              variant="secondary"
              size="sm"
              disabled={refreshing}
              onClick={refreshVerification}
              data-testid="onboarding-refresh-status-btn"
            >
              {refreshing ? 'Atualizando...' : 'Atualizar status'}
            </Button>
          }
        >
          {reviewing ? `Conferindo: ${reviewing}. ` : ''}
          Avisaremos por aqui quando a análise terminar. Atualizado há{' '}
          {Math.max(0, Math.floor((now - verification.dataUpdatedAt) / 60_000))} min.
        </Banner>
      ) : null}
      <ProfileSection
        verification={verification.data?.verification}
        onboarding={verification.data?.onboarding}
        rejectedField={verification.data?.submission.rejectedField ?? null}
        verificationUpdatedAt={verification.dataUpdatedAt}
        onProfileSaveSuccess={handleProfileSaveSuccess}
        editRequest={profileEditRequest}
        expanded={profileExpanded}
        onExpandedChange={setProfileExpanded}
      />
      <VerificationPanel
        verification={verification}
        enabled={verificationEnabled}
        activationPending={activationInProgress || enrollment.data?.status === 'creation_pending'}
        showBankAccount={false}
        onEditProfile={() => {
          setProfileExpanded(true)
          setProfileEditRequest((request) => request + 1)
        }}
      />
      <BankAccountSection
        bank={verification.data?.bank ?? null}
        identityPending={pendingVerification}
        onboarding={verification.data?.onboarding}
      />
      <OperationalStatusSection
        onboarding={verification.data?.onboarding}
        isSaleBlocked={user?.isSaleBlocked}
      />
    </div>
  )
}

function registrationSupportFeedback(
  code: KycOnboarding['nextStep']['code'] | undefined,
  configurationUnavailable: boolean,
  stale: boolean,
): { title: string; description: string } | null {
  if (configurationUnavailable) {
    return {
      title: 'Cadastro indisponível no momento',
      description: 'Fale com o suporte da VendePay para verificar a disponibilidade do cadastro.',
    }
  }
  switch (code) {
    case 'rejected':
      return {
        title: stale ? 'Última situação conhecida: cadastro não aprovado' : 'Cadastro não aprovado',
        description: 'Fale com o suporte da VendePay para orientação sobre seu cadastro.',
      }
    case 'activation_failed':
      return {
        title: 'Não foi possível concluir seu cadastro',
        description: 'Fale com o suporte da VendePay para verificar o que impede a conclusão.',
      }
    case 'unsupported_requirement':
      return {
        title: 'Uma pendência do cadastro precisa de atendimento',
        description: 'Fale com o suporte da VendePay para orientação sobre essa pendência.',
      }
    case 'account_disabled':
      return {
        title: stale
          ? 'Última situação conhecida: cadastro com restrição'
          : 'Cadastro com restrição',
        description: 'Fale com o suporte da VendePay para orientação sobre essa restrição.',
      }
    case 'sync_failing':
      return {
        title: 'A atualização do cadastro precisa de atendimento',
        description: 'Fale com o suporte da VendePay para verificar a falha de atualização.',
      }
    default:
      return null
  }
}
